import React, { useContext, useEffect, useState } from "react";
import { Box, Flex, Spinner } from "theme-ui";
import { themevals } from "theme/themevals";
import P from "components/typography/P";
import H5 from "components/typography/H5";
import BasicButton from "components/forms/BasicButton";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/ui/Modal";
import { ModalIcon, ModalSubtitle, ModalTitle, ModalTitleWrapper } from "components/ui/Header/styled";
import { BankIcon, CreditCardIcon, CheckMarkIcon, CloseIcon } from "components/svg";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import PlaidLinkButton from "components/PlaidLink";
import {
  getSavedPaymentMethods,
  createSetupIntent,
  saveCard,
  saveBankAccount,
  removeSavedMethod,
} from "api/Payments";
import UserContext from "context/User";
import { useToast } from "components/toast";

const PaymentSettings = () => {
  const toast = useToast();
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useContext<any>(UserContext);
  const isClientAdmin = user?.client?.companyAdmin === true;

  const [methods, setMethods] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Add card modal
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [addCardOwnerType, setAddCardOwnerType] = useState<"org" | "user">("user");
  const [addCardSubmitting, setAddCardSubmitting] = useState(false);

  // Add bank modal
  const [addBankOpen, setAddBankOpen] = useState(false);
  const [addBankOwnerType, setAddBankOwnerType] = useState<"org" | "user">("user");
  const [addBankSubmitting, setAddBankSubmitting] = useState(false);

  // Remove confirmation
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<any>(null);
  const [removeSubmitting, setRemoveSubmitting] = useState(false);

  useEffect(() => {
    _fetchMethods();
  }, []);

  const _fetchMethods = async () => {
    setLoading(true);
    try {
      const res = await getSavedPaymentMethods();
      setMethods(Array.isArray(res) ? res : []);
    } catch (e: any) {
      console.error("Error fetching methods:", e);
    } finally {
      setLoading(false);
    }
  };

  const formatMethodLabel = (method: any) => {
    if (method.methodType === "card") {
      const brand = (method.brand || "Card").charAt(0).toUpperCase() + (method.brand || "card").slice(1);
      return `${brand} ending in ${method.last4}`;
    }
    return `${method.bankName || "Bank"} account ending in ${method.last4}`;
  };

  // ─── Add Card Flow ───
  const _submitAddCard = async () => {
    if (!stripe || !elements) {
      toast.add("Stripe is not ready.", "var(--theme-ui-colors-danger)");
      return;
    }
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    setAddCardSubmitting(true);
    try {
      // 1. Create SetupIntent
      const siRes = await createSetupIntent();
      if (!siRes?.clientSecret) {
        toast.add("Failed to create setup intent.", "var(--theme-ui-colors-danger)");
        setAddCardSubmitting(false);
        return;
      }

      // 2. Confirm with Stripe
      const { error, setupIntent } = await stripe.confirmCardSetup(siRes.clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: user?.name || "Customer",
            email: user?.email || undefined,
          },
        },
      });

      if (error) {
        toast.add(error.message || "Card setup failed.", "var(--theme-ui-colors-danger)");
        setAddCardSubmitting(false);
        return;
      }

      if (setupIntent?.status === "succeeded") {
        // 3. Save in backend
        const saveRes = await saveCard({
          setupIntentId: setupIntent.id,
          ownerType: addCardOwnerType,
        });
        if (saveRes?.id) {
          toast.add("Card saved successfully.", "var(--theme-ui-colors-success)");
          setAddCardOpen(false);
          _fetchMethods();
        } else {
          toast.add(saveRes?.message || "Failed to save card.", "var(--theme-ui-colors-danger)");
        }
      }
    } catch (err: any) {
      console.error("Add card error:", err);
      toast.add("Error saving card.", "var(--theme-ui-colors-danger)");
    } finally {
      setAddCardSubmitting(false);
    }
  };

  // ─── Add Bank Flow ───
  const _handlePlaidSuccess = async (publicToken: string, metadata: any) => {
    setAddBankSubmitting(true);
    try {
      const res = await saveBankAccount({
        publicToken,
        accountId: metadata.account_id,
        ownerType: addBankOwnerType,
      });
      if (res?.id) {
        toast.add("Bank account saved successfully.", "var(--theme-ui-colors-success)");
        setAddBankOpen(false);
        _fetchMethods();
      } else {
        toast.add(res?.message || "Failed to save bank account.", "var(--theme-ui-colors-danger)");
      }
    } catch (err: any) {
      console.error("Add bank error:", err);
      toast.add("Error saving bank account.", "var(--theme-ui-colors-danger)");
    } finally {
      setAddBankSubmitting(false);
    }
  };

  // ─── Remove Flow ───
  const _confirmRemove = async () => {
    if (!removeTarget) return;
    setRemoveSubmitting(true);
    try {
      const res = await removeSavedMethod(removeTarget.id);
      if (res?.success !== false) {
        toast.add("Payment method removed.", "var(--theme-ui-colors-success)");
        setRemoveModalOpen(false);
        setRemoveTarget(null);
        _fetchMethods();
      } else {
        toast.add(res?.message || "Failed to remove method.", "var(--theme-ui-colors-danger)");
      }
    } catch (err: any) {
      console.error("Remove error:", err);
      toast.add("Error removing payment method.", "var(--theme-ui-colors-danger)");
    } finally {
      setRemoveSubmitting(false);
    }
  };

  // Split methods into org and personal
  const orgMethods = methods.filter((m: any) => m.ownerType === "org");
  const personalMethods = methods.filter((m: any) => m.ownerType === "user");

  const renderMethodRow = (method: any) => {
    const isCard = method.methodType === "card";
    const canRemove = method.ownerType === "user" || isClientAdmin;
    return (
      <Flex
        key={method.id}
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          backgroundColor: "#fff",
          borderRadius: "6px",
          border: `1px solid ${themevals.colors.inputOutline}`,
          "&:hover": { backgroundColor: "#fafbfc" },
          transition: "background-color 0.1s ease",
        }}
      >
        <Flex sx={{ alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}>
          <Box sx={{ width: "16px", height: "16px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {isCard ? <CreditCardIcon fill={themevals.colors.dkgrey} width="16px" height="16px" /> : <BankIcon fill={themevals.colors.dkgrey} width="16px" height="16px" />}
          </Box>
          <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" fontWeight="500" color={themevals.colors.black} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {method.label || formatMethodLabel(method)}
            <span style={{ color: themevals.colors.dkgrey, fontWeight: 400, marginLeft: "8px", fontSize: "12px" }}>
              {isCard && method.expiryMonth && method.expiryYear
                ? `Exp ${String(method.expiryMonth).padStart(2, "0")}/${method.expiryYear}`
                : isCard
                ? "Card"
                : "Bank account"}
            </span>
          </P>
        </Flex>
        {canRemove && (
          <BasicButton
            type="button"
            scheme="secondaryOutline"
            onClick={() => {
              setRemoveTarget(method);
              setRemoveModalOpen(true);
            }}
            styles={{ padding: "5px 12px", fontSize: "12px", flexShrink: "0", marginLeft: "12px" }}
          >
            Remove
          </BasicButton>
        )}
      </Flex>
    );
  };

  const OwnerTypeToggle = ({
    value,
    onChange,
  }: {
    value: "org" | "user";
    onChange: (v: "org" | "user") => void;
  }) => {
    if (!isClientAdmin) return null;
    return (
      <Flex sx={{ gap: "8px", marginBottom: "20px" }}>
        <BasicButton
          type="button"
          scheme={value === "user" ? "primary" : "secondaryOutline"}
          onClick={() => onChange("user")}
          styles={{ padding: "6px 14px", fontSize: "13px" }}
        >
          Personal
        </BasicButton>
        <BasicButton
          type="button"
          scheme={value === "org" ? "primary" : "secondaryOutline"}
          onClick={() => onChange("org")}
          styles={{ padding: "6px 14px", fontSize: "13px" }}
        >
          Organization
        </BasicButton>
      </Flex>
    );
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", padding: "40px" }}>
        <Spinner size={25} strokeWidth={3} title="loading" />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: "700px" }}>
      <H5 fontFamily="Rubik" fontSize="16px" marginLeft="0px" marginTop="0px">
        Payment Methods
      </H5>
      <P marginTop="0px" marginBottom="24px" fontFamily="Poppins" fontSize="14px" color={themevals.colors.dkgrey}>
        Manage saved payment methods for paying invoices. Saved methods allow one-click payments on future invoices.
      </P>

      {/* Organization methods - visible to all, manageable by admins */}
      {(isClientAdmin || orgMethods.length > 0) && (
        <Box sx={{ marginBottom: "32px" }}>
          <P marginTop="0px" marginBottom="12px" fontFamily="Poppins" fontSize="13px" fontWeight="600" color={themevals.colors.dkgrey}>
            Organization Payment Methods
          </P>
          <Flex sx={{ flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
            {orgMethods.length === 0 && (
              <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" color={themevals.colors.dkgrey}>
                No organization payment methods saved.
              </P>
            )}
            {orgMethods.map(renderMethodRow)}
          </Flex>
          {isClientAdmin && (
            <Flex sx={{ gap: "12px" }}>
              <BasicButton
                type="button"
                scheme="secondaryOutline"
                onClick={() => {
                  setAddCardOwnerType("org");
                  setAddCardOpen(true);
                }}
                styles={{ padding: "8px 16px", fontSize: "13px" }}
              >
                <CreditCardIcon fill={themevals.colors.black} width="14px" height="14px" /> Add Card
              </BasicButton>
              <BasicButton
                type="button"
                scheme="secondaryOutline"
                onClick={() => {
                  setAddBankOwnerType("org");
                  setAddBankOpen(true);
                }}
                styles={{ padding: "8px 16px", fontSize: "13px" }}
              >
                <BankIcon fill={themevals.colors.black} width="14px" height="14px" /> Add Bank Account
              </BasicButton>
            </Flex>
          )}
        </Box>
      )}

      {/* Personal methods */}
      <Box sx={{ marginBottom: "32px" }}>
        <P marginTop="0px" marginBottom="12px" fontFamily="Poppins" fontSize="13px" fontWeight="600" color={themevals.colors.dkgrey}>
          Your Payment Methods
        </P>
        <Flex sx={{ flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
          {personalMethods.length === 0 && (
            <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" color={themevals.colors.dkgrey}>
              No personal payment methods saved.
            </P>
          )}
          {personalMethods.map(renderMethodRow)}
        </Flex>
        <Flex sx={{ gap: "12px" }}>
          <BasicButton
            type="button"
            scheme="secondaryOutline"
            onClick={() => {
              setAddCardOwnerType("user");
              setAddCardOpen(true);
            }}
            styles={{ padding: "8px 16px", fontSize: "13px" }}
          >
            <CreditCardIcon fill={themevals.colors.black} width="14px" height="14px" /> Add Card
          </BasicButton>
          <BasicButton
            type="button"
            scheme="secondaryOutline"
            onClick={() => {
              setAddBankOwnerType("user");
              setAddBankOpen(true);
            }}
            styles={{ padding: "8px 16px", fontSize: "13px" }}
          >
            <BankIcon fill={themevals.colors.black} width="14px" height="14px" /> Add Bank Account
          </BasicButton>
        </Flex>
      </Box>

      {/* ─── Add Card Modal ─── */}
      <Modal isOpen={addCardOpen} toggle={() => setAddCardOpen(false)} centered closeOnBackdropClick={false}>
        <ModalHeader toggle={() => setAddCardOpen(false)}>
          <ModalIcon>
            <CreditCardIcon fill={"000"} width="20px" height="20px" />
          </ModalIcon>
          <ModalTitleWrapper>
            <ModalTitle>Add Credit Card</ModalTitle>
            <ModalSubtitle>Save a card for future invoice payments.</ModalSubtitle>
          </ModalTitleWrapper>
        </ModalHeader>
        <ModalBody>
          {isClientAdmin && (
            <OwnerTypeToggle value={addCardOwnerType} onChange={setAddCardOwnerType} />
          )}
          <P marginTop="0px" marginBottom="16px" fontFamily="Poppins" fontSize="13px" color={themevals.colors.dkgrey}>
            {addCardOwnerType === "org"
              ? "This card will be available to all users in your organization."
              : "This card will only be available to you."}
          </P>
          <Box sx={{ mb: 3, p: 2, border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", background: "#fff" }}>
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: "14px",
                    fontFamily: "Poppins",
                    color: "#000",
                    backgroundColor: "#fff",
                    "::placeholder": { color: themevals.colors.dkgrey },
                  },
                  invalid: {
                    color: themevals.colors.danger,
                  },
                },
              }}
            />
          </Box>
        </ModalBody>
        <ModalFooter>
          <BasicButton type="button" onClick={() => setAddCardOpen(false)} scheme="secondaryOutline">
            Cancel
          </BasicButton>
          <BasicButton
            type="button"
            onClick={_submitAddCard}
            $submitting={addCardSubmitting}
            $iconHeight="14px"
            $iconWidth="14px"
            $loadingColor="#fff"
            keepText={true}
            styles={{ marginLeft: "10px" }}
          >
            {!addCardSubmitting && <CheckMarkIcon fill={"#fff"} />}
            Save Card
          </BasicButton>
        </ModalFooter>
      </Modal>

      {/* ─── Add Bank Account Modal ─── */}
      <Modal isOpen={addBankOpen} toggle={() => setAddBankOpen(false)} centered closeOnBackdropClick={false}>
        <ModalHeader toggle={() => setAddBankOpen(false)}>
          <ModalIcon>
            <BankIcon fill={"000"} width="20px" height="20px" />
          </ModalIcon>
          <ModalTitleWrapper>
            <ModalTitle>Add Bank Account</ModalTitle>
            <ModalSubtitle>Save a bank account for future ACH payments.</ModalSubtitle>
          </ModalTitleWrapper>
        </ModalHeader>
        <ModalBody>
          {isClientAdmin && (
            <OwnerTypeToggle value={addBankOwnerType} onChange={setAddBankOwnerType} />
          )}
          <P marginTop="0px" marginBottom="16px" fontFamily="Poppins" fontSize="13px" color={themevals.colors.dkgrey}>
            {addBankOwnerType === "org"
              ? "This bank account will be available to all users in your organization."
              : "This bank account will only be available to you."}
          </P>
          <P marginTop="0px" marginBottom="20px" fontFamily="Poppins" fontSize="14px" lineHeight="22px" color={themevals.colors.black}>
            Connect your bank account securely using Plaid. Your banking information is never stored on our servers.
          </P>
          <Flex sx={{ justifyContent: "center" }}>
            {addBankSubmitting && <Spinner size={24} color={themevals.colors.primary} />}
          </Flex>
        </ModalBody>
        <ModalFooter>
          <BasicButton type="button" onClick={() => setAddBankOpen(false)} scheme="secondaryOutline">
            Cancel
          </BasicButton>
          <PlaidLinkButton onSuccess={_handlePlaidSuccess} buttonText="Connect Your Bank" isSubmitting={addBankSubmitting} />
        </ModalFooter>
      </Modal>

      {/* ─── Remove Confirmation Modal ─── */}
      <Modal isOpen={removeModalOpen} toggle={() => { setRemoveModalOpen(false); setRemoveTarget(null); }} centered>
        <ModalHeader toggle={() => { setRemoveModalOpen(false); setRemoveTarget(null); }}>
          <ModalIcon>
            <CloseIcon fill={themevals.colors.danger} />
          </ModalIcon>
          <ModalTitleWrapper>
            <ModalTitle>Remove Payment Method</ModalTitle>
            <ModalSubtitle>This action cannot be undone.</ModalSubtitle>
          </ModalTitleWrapper>
        </ModalHeader>
        <ModalBody>
          {removeTarget && (
            <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="14px" lineHeight="22px" color={themevals.colors.black}>
              Are you sure you want to remove <strong>{removeTarget.label || formatMethodLabel(removeTarget)}</strong>? 
              This will detach it from Stripe and it can no longer be used for payments.
            </P>
          )}
        </ModalBody>
        <ModalFooter>
          <BasicButton type="button" onClick={() => { setRemoveModalOpen(false); setRemoveTarget(null); }} scheme="secondaryOutline">
            Cancel
          </BasicButton>
          <BasicButton
            type="button"
            scheme="danger"
            onClick={_confirmRemove}
            $submitting={removeSubmitting}
            $iconHeight="14px"
            $iconWidth="14px"
            $loadingColor="#fff"
            keepText={true}
            styles={{ marginLeft: "10px" }}
          >
            {!removeSubmitting && <CloseIcon fill={"#fff"} />}
            Remove
          </BasicButton>
        </ModalFooter>
      </Modal>
    </Box>
  );
};

export default PaymentSettings;
